/**
 - 닷패드 실시간 전송 경로 진단용 트레이서
 *
 - 마우스 드로잉은 초당 수십 번 도는 경로라 이벤트마다 console.log 를 찍으면
 - 콘솔이 묻히는 것은 물론이고 devtools 가 열린 상태에서는 BLE 전송 속도까지 떨어진다.
 - 그래서 모든 이벤트는 링 버퍼에만 쌓고, 출력이 실제로 멈춘 순간(STALL)처럼
 - 드물게 발생하는 사건만 즉시 콘솔에 찍는다.
 *
 - 사용법 (devtools 콘솔)
 -   dotPadTrace.enabled = false   // 계측 끄기
 -   dotPadTrace.echo = true       // 모든 이벤트를 즉시 출력 (느려짐, 정밀 추적용)
 -   dotPadTrace.dump()            // 지금까지 쌓인 이벤트 표로 출력
 -   dotPadTrace.text()            // 붙여넣기용 텍스트로 반환
 */

const RING_SIZE = 600

class DotPadTrace {
    constructor() {
        // 개발 모드에서만 기본으로 켠다. 배포본에서는 필요할 때 콘솔에서 dotPadTrace.enabled = true 로 켠다.
        this.enabled = process.env.NODE_ENV !== "production"
        // true 면 모든 이벤트를 즉시 콘솔에 출력한다. 전송 속도에 영향을 주므로 기본은 false.
        this.echo = false
        // 이상 상황에서 자동 덤프를 허용할 횟수.
        // console.table 로 수백 행을 찍는 것 자체가 메인 스레드를 수십 ms 잡아먹어
        // BLE ACK 처리가 밀린다. 즉 무제한 자동 덤프는 진단하려던 증상을 스스로 만든다.
        this.autoDumpBudget = 1
        this.ring = new Array(RING_SIZE)
        this.head = 0
        this.size = 0
        this.counters = Object.create(null)
        this.t0 = this.now()
        this.lastAt = this.t0
    }

    /**
     - 이상 상황용 자동 덤프.
     - 직전 구간만 한 번의 console.log 로 찍는다. (console.table 수백 행은 그 자체로 메인 스레드를 잡는다)
     */
    autoDump(reason, count = 25) {
        if (!this.enabled) return
        if (this.autoDumpBudget <= 0) return

        this.autoDumpBudget -= 1
        console.log(`[dotpad] 최근 ${count}건 (${reason})\n${this.text(count)}`)
        if (this.autoDumpBudget <= 0) {
            console.log("[dotpad] 자동 덤프는 여기까지. 이후는 dotPadTrace.dump() / dotPadTrace.text() 로 확인")
        }
    }

    now() {
        return typeof performance !== "undefined" && performance.now
            ? performance.now()
            : Date.now()
    }

    /**
     - 이벤트를 링 버퍼에 기록한다. (고빈도 이벤트용)
     - data 로 함수를 넘기면 계측이 켜져 있을 때만 실행한다.
     -   꺼진 상태에서도 인자는 먼저 평가되므로, 상태 스냅샷처럼 만드는 데 비용이 드는 값은
     -   반드시 함수로 넘겨야 배포본에서 오버헤드가 남지 않는다.
     */
    log(tag, data) {
        if (!this.enabled) return

        const at = this.now()
        const entry = {
            t: Math.round(at - this.t0),
            d: Math.round(at - this.lastAt),
            tag,
            data: typeof data === "function" ? data() : data
        }
        this.lastAt = at

        this.ring[this.head] = entry
        this.head = (this.head + 1) % RING_SIZE
        if (this.size < RING_SIZE) this.size += 1

        if (this.echo) {
            console.log(`[dotpad] +${entry.d}ms ${tag}`, entry.data ?? "")
        }

        return entry
    }

    /**
     - 이벤트를 기록하면서 즉시 콘솔에도 출력한다. (저빈도 · 이상 상황용)
     */
    loud(tag, data) {
        if (!this.enabled) return

        const entry = this.log(tag, data)
        if (!this.echo && entry) {
            console.log(`[dotpad] +${entry.d}ms ${tag}`, entry.data ?? "")
        }
        return entry
    }

    count(name, delta = 1) {
        if (!this.enabled) return
        this.counters[name] = (this.counters[name] ?? 0) + delta
    }

    entries() {
        const list = []
        const start = this.size < RING_SIZE ? 0 : this.head
        for (let i = 0; i < this.size; i++) {
            list.push(this.ring[(start + i) % RING_SIZE])
        }
        return list
    }

    text(count) {
        const list = this.entries()
        const target = count > 0 ? list.slice(-count) : list
        return target
            .map((e) => `${e.t}\t+${e.d}\t${e.tag}\t${e.data ? JSON.stringify(e.data) : ""}`)
            .join("\n")
    }

    /**
     - 쌓인 이벤트를 한 번에 출력한다.
     */
    dump(reason = "manual") {
        if (!this.enabled) return

        const list = this.entries()
        console.groupCollapsed(`[dotpad] trace dump (${reason}) - ${list.length}건`)
        console.table(list.map((e) => ({
            t: e.t,
            "+ms": e.d,
            tag: e.tag,
            data: e.data ? JSON.stringify(e.data) : ""
        })))
        console.log("counters", { ...this.counters })
        console.groupEnd()
    }

    reset() {
        this.ring = new Array(RING_SIZE)
        this.head = 0
        this.size = 0
        this.counters = Object.create(null)
        this.t0 = this.now()
        this.lastAt = this.t0
    }
}

const dotPadTrace = new DotPadTrace()

// 메인 스레드가 막힌 구간을 기록한다.
// BLE ACK 는 characteristicvaluechanged 이벤트로 들어오므로, 렌더링이나 픽셀 변환으로
// 메인 스레드가 점유되면 기기가 제때 응답해도 우리 쪽에서 늦게 받는다.
// 즉 "ACK 가 늦다"가 기기 문제인지 우리 문제인지는 이 값과 대조해야 갈린다.
if (typeof PerformanceObserver !== "undefined") {
    try {
        const observer = new PerformanceObserver((list) => {
            for (const entry of list.getEntries()) {
                dotPadTrace.log("longtask", { ms: Math.round(entry.duration) })
            }
        })
        observer.observe({ entryTypes: ["longtask"] })
    } catch (e) {
        // longtask 미지원 브라우저는 무시
    }
}

if (typeof window !== "undefined") {
    window.dotPadTrace = dotPadTrace
}

export default dotPadTrace
